import React, { useEffect, useState } from 'react';
import { Save, X } from 'lucide-react';

import FormModal from './FormModal';

import ActionButton from '../Buttons/ActionButton';

import InputField from '../Forms/Input/InputField';
import InputIconByKey from '../Forms/Input/InputIconByKey';
import TextAreaField from '../Forms/Input/TextAreaField';

import DatePicker from '../Forms/DatePicker/DatePicker';
import TimePicker from '../Forms/Time/TimePicker';

import { validateSaleSupportReason } from '../../features/sale/support/saleSupportValidation';

// =====================================================
// 📦 DTO
// =====================================================

export interface SaleSupportPatch {
  saleStatus?: string;
  dteStatus?: string;

  generationDate?: string;
  generationTime?: string;

  generationCode?: string;
  receptionSeal?: string;
  controlNumber?: string;

  processingDate?: string;

  reason: string;
}

// =====================================================
// 📦 Props
// =====================================================

interface SaleSupportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: SaleSupportPatch) => Promise<void>;
}

// =====================================================
// 📦 Estado interno
// =====================================================

interface SupportValues {
  saleStatus: string;
  dteStatus: string;

  generationDate: string;
  generationTime: string;

  generationCode: string;
  receptionSeal: string;
  controlNumber: string;

  processingDate: string;
}

const EMPTY_VALUES: SupportValues = {
  saleStatus: '',
  dteStatus: '',

  generationDate: '',
  generationTime: '',

  generationCode: '',
  receptionSeal: '',
  controlNumber: '',

  processingDate: '',
};

// =====================================================
// 🧩 Componente
// =====================================================

const SaleSupportModal: React.FC<SaleSupportModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [values, setValues] =
    useState<SupportValues>(EMPTY_VALUES);

  const [reason, setReason] = useState('');

  const [saving, setSaving] = useState(false);

  const [reasonError, setReasonError] = useState('');
  const [formError, setFormError] = useState('');

  // =====================================================
  // 🔄 Reiniciar formulario
  // =====================================================

  useEffect(() => {
    if (!isOpen) return;

    setValues(EMPTY_VALUES);
    setReason('');

    setReasonError('');
    setFormError('');

    setSaving(false);
  }, [isOpen]);

  // =====================================================
  // ✏️ Actualizar campo
  // =====================================================

  const updateValue = (
    field: keyof SupportValues,
    value: string
  ) => {
    setValues((previous) => ({
      ...previous,
      [field]: value,
    }));

    if (formError) {
      setFormError('');
    }
  };

  // =====================================================
  // ❌ Cerrar
  // =====================================================

  const handleClose = () => {
    if (saving) return;

    onClose();
  };

  // =====================================================
  // 💾 Guardar
  // =====================================================

  const handleSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();

    setReasonError('');
    setFormError('');

    // -----------------------------------------------------
    // 1. Validar razón
    // -----------------------------------------------------

    const cleanReason = reason.trim();

    const validationError =
      validateSaleSupportReason(cleanReason);

    if (validationError) {
      setReasonError(validationError);
      return;
    }

    // -----------------------------------------------------
    // 2. Obtener únicamente campos informados
    // -----------------------------------------------------

    const changedFields = Object.fromEntries(
      Object.entries(values).filter(
        ([, value]) => value.trim() !== ''
      )
    ) as Partial<SupportValues>;

    if (Object.keys(changedFields).length === 0) {
      setFormError(
        'Debe indicar al menos un campo que desea corregir.'
      );

      return;
    }

    // -----------------------------------------------------
    // 3. Confirmación
    // -----------------------------------------------------

    const confirmed = window.confirm(
      'Este cambio modificará información sensible de la venta y quedará auditado. ¿Desea guardar la corrección?'
    );

    if (!confirmed) return;

    // -----------------------------------------------------
    // 4. Enviar
    // -----------------------------------------------------

    setSaving(true);

    try {
      await onSubmit({
        ...changedFields,
        reason: cleanReason,
      });
    } catch (submitError) {
      const message =
        submitError instanceof Error
          ? submitError.message
          : 'No fue posible guardar la corrección de soporte.';

      setFormError(message);
    } finally {
      setSaving(false);
    }
  };

  // =====================================================
  // 🎨 Render
  // =====================================================

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Corrección de soporte"
      maxWidth="max-w-4xl"
      overlayBg="black"
      zIndex="front"
    >
      <form
        onSubmit={handleSubmit}
        className="w-full"
      >
        {/* =================================================
            DESCRIPCIÓN
            ================================================= */}

        <div className="mb-6">
          <p className="text-sm leading-6 text-body dark:text-bodydark">
            Corrija información administrativa o fiscal de la
            venta. Únicamente se enviarán los campos que sean
            informados.
          </p>
        </div>

        {/* =================================================
            ADVERTENCIA
            ================================================= */}

        <div
          className="
            mb-7
            flex
            items-start
            gap-3

            rounded-xl
            border
            border-warning/30

            bg-warning/10

            px-4
            py-3.5
          "
        >
          <div
            className="
              mt-0.5
              flex
              h-9
              w-9
              shrink-0
              items-center
              justify-center

              rounded-lg

              bg-warning/15
              text-warning
            "
          >
            <InputIconByKey
              iconKey="warning"
              className="h-5 w-5 text-warning"
            />
          </div>

          <div>
            <p className="text-sm font-semibold text-black dark:text-white">
              Operación auditada
            </p>

            <p className="mt-1 text-xs leading-5 text-body dark:text-bodydark">
              Todos los cambios realizados mediante esta
              herramienta quedarán registrados en el historial
              de soporte. Utilícela únicamente para correcciones
              justificadas.
            </p>
          </div>
        </div>

        {/* =================================================
            ERROR GENERAL
            ================================================= */}

        {formError && (
          <div
            className="
              mb-6
              flex
              items-start
              gap-3

              rounded-xl
              border
              border-danger/30

              bg-danger/5

              px-4
              py-3
            "
          >
            <InputIconByKey
              iconKey="alert"
              className="mt-0.5 h-5 w-5 shrink-0 text-danger"
            />

            <p className="text-sm text-danger">
              {formError}
            </p>
          </div>
        )}

        {/* =================================================
            1. ESTADOS
            ================================================= */}

        <section className="mb-7">
          <div
            className="
              mb-5
              flex
              items-center
              gap-3

              border-b
              border-stroke

              pb-3

              dark:border-strokedark
            "
          >
            <div
              className="
                flex
                h-9
                w-9
                shrink-0
                items-center
                justify-center

                rounded-xl

                bg-primary/10
              "
            >
              <InputIconByKey
                iconKey="status"
                className="h-5 w-5 text-primary"
              />
            </div>

            <div>
              <h3 className="text-base font-semibold text-black dark:text-white">
                Estados
              </h3>

              <p className="mt-0.5 text-xs text-body dark:text-bodydark">
                Corrija únicamente el estado que requiera
                intervención de soporte.
              </p>
            </div>
          </div>

          <div className="grid gap-x-5 sm:grid-cols-2">
            <InputField
              label="Estado de venta"
              name="saleStatus"
              type="text"
              value={values.saleStatus}
              placeholder="Ej. FINALIZED"
              iconKey="status"
              disabled={saving}
              onChange={(event) =>
                updateValue(
                  'saleStatus',
                  event.target.value
                )
              }
            />

            <InputField
              label="Estado DTE"
              name="dteStatus"
              type="text"
              value={values.dteStatus}
              placeholder="Ej. PROCESSED"
              iconKey="status"
              disabled={saving}
              onChange={(event) =>
                updateValue(
                  'dteStatus',
                  event.target.value
                )
              }
            />
          </div>
        </section>

        {/* =================================================
            2. INFORMACIÓN DE GENERACIÓN
            ================================================= */}

        <section className="mb-7">
          <div
            className="
              mb-5
              flex
              items-center
              gap-3

              border-b
              border-stroke

              pb-3

              dark:border-strokedark
            "
          >
            <div
              className="
                flex
                h-9
                w-9
                shrink-0
                items-center
                justify-center

                rounded-xl

                bg-primary/10
              "
            >
              <InputIconByKey
                iconKey="document"
                className="h-5 w-5 text-primary"
              />
            </div>

            <div>
              <h3 className="text-base font-semibold text-black dark:text-white">
                Información de generación
              </h3>

              <p className="mt-0.5 text-xs text-body dark:text-bodydark">
                Información asociada a la generación y
                procesamiento del Documento Tributario
                Electrónico.
              </p>
            </div>
          </div>

          {/* Fecha + hora */}

          <div className="grid gap-x-5 sm:grid-cols-2">
            <DatePicker
              label="Fecha de generación"
              name="generationDate"
              value={values.generationDate}
              placeholder="Seleccione la fecha"
              iconKey="calendar"
              disabled={saving}
              onChange={(value) =>
                updateValue(
                  'generationDate',
                  value
                )
              }
            />

            <TimePicker
              label="Hora de generación"
              name="generationTime"
              value={values.generationTime}
              placeholder="Seleccione la hora"
              iconKey="clock"
              disabled={saving}
              onChange={(value) =>
                updateValue(
                  'generationTime',
                  value
                )
              }
            />
          </div>

          {/* Código */}

          <InputField
            label="Código de generación"
            name="generationCode"
            type="text"
            value={values.generationCode}
            placeholder="UUID del Documento Tributario Electrónico"
            iconKey="code"
            disabled={saving}
            onChange={(event) =>
              updateValue(
                'generationCode',
                event.target.value
              )
            }
          />

          {/* Sello */}

          <InputField
            label="Sello de recepción"
            name="receptionSeal"
            type="text"
            value={values.receptionSeal}
            placeholder="Sello asignado por el Ministerio de Hacienda"
            iconKey="shield"
            disabled={saving}
            onChange={(event) =>
              updateValue(
                'receptionSeal',
                event.target.value
              )
            }
          />

          {/* Número control */}

          <InputField
            label="Número de control"
            name="controlNumber"
            type="text"
            value={values.controlNumber}
            placeholder="Ej. DTE-01-M001P001-000000000000001"
            iconKey="hash"
            disabled={saving}
            onChange={(event) =>
              updateValue(
                'controlNumber',
                event.target.value
              )
            }
          />

          {/* Procesamiento */}

          <DatePicker
            label="Fecha de procesamiento"
            name="processingDate"
            value={values.processingDate}
            placeholder="Seleccione fecha y hora"
            enableTime
            iconKey="calendar"
            disabled={saving}
            onChange={(value) =>
              updateValue(
                'processingDate',
                value
              )
            }
          />
        </section>

        {/* =================================================
            3. JUSTIFICACIÓN
            ================================================= */}

        <section>
          <div
            className="
              mb-5
              flex
              items-center
              gap-3

              border-b
              border-stroke

              pb-3

              dark:border-strokedark
            "
          >
            <div
              className="
                flex
                h-9
                w-9
                shrink-0
                items-center
                justify-center

                rounded-xl

                bg-primary/10
              "
            >
              <InputIconByKey
                iconKey="note"
                className="h-5 w-5 text-primary"
              />
            </div>

            <div>
              <h3 className="text-base font-semibold text-black dark:text-white">
                Justificación
              </h3>

              <p className="mt-0.5 text-xs text-body dark:text-bodydark">
                Indique claramente el motivo por el cual se
                realiza esta corrección.
              </p>
            </div>
          </div>

          <TextAreaField
            label="Razón de la corrección"
            name="reason"
            value={reason}
            required
            rows={4}
            maxLength={500}
            iconKey="note"
            disabled={saving}
            placeholder="Describa el motivo de la corrección realizada..."
            error={reasonError}
            helperText={
              !reasonError
                ? 'Esta justificación quedará almacenada como parte de la auditoría.'
                : undefined
            }
            rightContent={
              <span>
                {reason.length}/500
              </span>
            }
            onChange={(event) => {
              setReason(event.target.value);

              if (reasonError) {
                setReasonError('');
              }

              if (formError) {
                setFormError('');
              }
            }}
          />
        </section>

        {/* =================================================
            ACCIONES
            ================================================= */}

        <div
          className="
            mt-7
            flex
            flex-col-reverse
            gap-3

            border-t
            border-stroke

            pt-5

            dark:border-strokedark

            sm:flex-row
            sm:justify-end
          "
        >
          <div className="w-full sm:w-auto">
            <ActionButton
              type="button"
              label="Cancelar"
              variant="secondary"
              icon={<X className="h-4 w-4" />}
              disabled={saving}
              fullWidth={false}
              onClick={handleClose}
            />
          </div>

          <div className="w-full sm:w-auto">
            <ActionButton
              type="submit"
              label="Guardar corrección"
              loadingLabel="Guardando..."
              variant="primary"
              icon={<Save className="h-4 w-4" />}
              loading={saving}
              fullWidth={false}
            />
          </div>
        </div>
      </form>
    </FormModal>
  );
};

export default SaleSupportModal;